import React from 'react';
import ChatSocket from './ChatSocket';
import config from '$/config';
import { logger } from '$/utils/logger';
import useRunOnce from '$/hooks/useRunOnce';
import { SocketNotification } from '$/GraphQL/schema/types';
import { withWebinarPlayerContext } from '../WebinarPlayer/context';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { SocketContext, SocketStatus } from './SocketContext';
interface SocketProps {
  uri: string;
  token?: string;
  refreshToken?: string;
  skipConnect?: boolean;
  onConnect?: (socket: ChatSocket) => void;
  children?: React.ReactNode;
}

const ConnectedEvent: SocketNotification = {
  socketConnected: true,
};

export const Socket: React.FC<SocketProps> = withWebinarPlayerContext([
  'connectionId',
  'setConnectionId',
])((props) => {
  const lastReceivedMessageTime = React.useRef<Date>(new Date());
  const { children, skipConnect, onConnect, context: webinarPlayerContext } = props;
  const { connectionId, setConnectionId } = webinarPlayerContext;
  const [status, setStatus] = React.useState(SocketStatus.Connecting);
  const [socket, setSocket] = React.useState<ChatSocket | null>(null);
  const onConnectRef = React.useRef(onConnect);

  React.useEffect(() => {
    onConnectRef.current = onConnect;
  }, [onConnect]);

  const hasSkipConnectChanged = useHasChangedShallow([skipConnect]);

  const reConnect = React.useCallback(
    (force?: boolean): void => {
      const keepAliveExpired =
        Date.now() - lastReceivedMessageTime.current.getTime() > config.SOCKET_KEEP_ALIVE_INTERVAL; // 5 minutes
      if ((force || keepAliveExpired || !socket?.isConnected) && !skipConnect) {
        console.debug('CHAT', 'CS -> reconnect socket');
        socket?.open();
      }
    },
    [skipConnect, socket]
  );

  React.useEffect(() => {
    if (!socket || !hasSkipConnectChanged) {
      return;
    }

    if (!skipConnect) {
      socket.open();
    }
  }, [hasSkipConnectChanged, skipConnect, socket]);

  useRunOnce(() => {
    const newSocket = new ChatSocket(`${config.CHAT_SERVER_URL}`, {
      skipConnect,
      onopen: (e): void => {
        if (!newSocket) {
          logger.error('No socket onopen');
          return;
        }

        logger.debug('CHAT', 'Socket connected:', JSON.stringify(e));
        onConnectRef.current?.(newSocket);
        // Send Connected event
        newSocket.onMessage({
          data: JSON.stringify(ConnectedEvent),
        });
        setStatus(SocketStatus.Connected);
      },
      onreconnect: (e): void => {
        setStatus(SocketStatus.Connecting);
        logger.debug('CHAT', 'Socket reconnecting...', e);
      },
      onclose: (e): void => {
        setStatus(SocketStatus.Closed);
        logger.debug('CHAT', 'Socket closed!', e);
      },
      onerror: (e): void => {
        setStatus(SocketStatus.Failed);
        logger.debug('CHAT', 'Socket error:', e);
      },
      onmessage: (notification): void => {
        try {
          lastReceivedMessageTime.current = new Date();
          if (notification.connectionId) {
            logger.debug('CHAT', 'NEW CID', notification.connectionId);
            setConnectionId(notification.connectionId);
          }
        } catch {}
      },
    });

    setSocket(newSocket);

    return (): void => {
      // We must use newSocket here - the local var - not socket the state that could be gone already
      newSocket.terminate();
    };
  });

  React.useEffect(() => {
    const interval = setInterval(reConnect, config.SOCKET_KEEP_ALIVE_INTERVAL / 10); // check every 30 seconds
    const onOnline = (): void => reConnect(true);
    const onVisibilityChange = (): void => {
      if (window.document.visibilityState !== 'visible') {
        return;
      }
      // reconnect socket when resume browser if it was closed
      reConnect();
    };

    if (window) {
      // If the network backs to online, reconnect the socket
      window.addEventListener('online', onOnline);
      // If the window has been in background too long video may be out of place
      window.document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return (): void => {
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [reConnect, skipConnect, socket]);

  const value = React.useMemo(
    () => ({ socket, status, connectionId, reConnect }),
    [socket, status, connectionId, reConnect]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
});

export default Socket;
